/* eslint-disable no-param-reassign */

import { api } from "rdx/services/api";
import { actions } from "rdx";
import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { camelizeKeys, keysToSnakeCase } from "lib/helpers/convertObjKeys";
import {
  GetProjectBlockerQueryParamsT,
  GetProjectBlockerResponseT,
  GetProjectBlockersQueryParamsT,
  GetProjectBlockersResponseT,
  CreateMessageMutationParamsT,
  CreateAssetMutationParamsT,
  UpdateDesignAnswersMutationParamsT,
} from "./types";
import { GetProjectBlockerResponseSchema, GetProjectBlockersResponseSchema } from "./schemas";
import { PROJECT_BLOCKER_TAGS } from "./tags";

export const projectBlockersApi = api
  .enhanceEndpoints({ addTagTypes: [...Object.values(PROJECT_BLOCKER_TAGS)] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getProjectBlockers: builder.query<GetProjectBlockersResponseT["body"], GetProjectBlockersQueryParamsT>({
        query: ({ projectId, isEnterprise = false }) => ({
          url: `projects/${projectId}/blockers`,
          params: { enterprise: isEnterprise },
        }),
        transformResponse: (res: GetProjectBlockersResponseT) => {
          SchemaValidator.validate<typeof GetProjectBlockersResponseSchema>({
            schema: GetProjectBlockersResponseSchema,
            schemaName: "GetProjectBlockersResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        providesTags: [PROJECT_BLOCKER_TAGS.PROJECT_BLOCKERS],
      }),
      getProjectBlocker: builder.query<GetProjectBlockerResponseT["body"], GetProjectBlockerQueryParamsT>({
        query: ({ projectId, blockerId }) => `projects/${projectId}/blockers/${blockerId}`,
        transformResponse: (res: GetProjectBlockerResponseT) => {
          SchemaValidator.validate<typeof GetProjectBlockerResponseSchema>({
            schema: GetProjectBlockerResponseSchema,
            schemaName: "GetProjectBlockerResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          queryFulfilled.catch(({ error }) => {
            const fetchError = error as { data: { error: { message: string } } };
            dispatch(
              actions.setAlertMessageVisible({
                message: fetchError?.data?.error.message || "There was an error retrieving blocker details",
                severity: "error",
              }),
            );
          });
        },
        providesTags: [PROJECT_BLOCKER_TAGS.PROJECT_BLOCKER],
      }),
      createProjectBlockerMessage: builder.mutation<GetProjectBlockerResponseT["body"], CreateMessageMutationParamsT>({
        query: ({ blockerId, projectId, params }) => ({
          url: `projects/${projectId}/blockers/${blockerId}/message`,
          method: "POST",
          body: keysToSnakeCase(params),
        }),
        transformResponse: (res: GetProjectBlockerResponseT) => {
          SchemaValidator.validate<typeof GetProjectBlockerResponseSchema>({
            schema: GetProjectBlockerResponseSchema,
            schemaName: "GetProjectBlockerResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: ({ projectId, blockerId }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(({ data }) => {
            dispatch(projectBlockersApi.util.upsertQueryData("getProjectBlocker", { projectId, blockerId }, data));
          });
        },
      }),
      createProjectBlockerAsset: builder.mutation<GetProjectBlockerResponseT["body"], CreateAssetMutationParamsT>({
        query: ({ projectId, params }) => ({
          url: `provider_leads/${projectId}/assets/blocker`,
          method: "POST",
          body: keysToSnakeCase(params),
        }),
        transformResponse: (res: GetProjectBlockerResponseT) => {
          SchemaValidator.validate<typeof GetProjectBlockerResponseSchema>({
            schema: GetProjectBlockerResponseSchema,
            schemaName: "GetProjectBlockerResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: ({ projectId, params: { blockerId } }, { dispatch, queryFulfilled }) => {
          queryFulfilled.then(({ data }) => {
            dispatch(projectBlockersApi.util.upsertQueryData("getProjectBlocker", { projectId, blockerId }, data));
          });
        },
      }),
      updateDesignAnswers: builder.mutation<GetProjectBlockersResponseT["body"], UpdateDesignAnswersMutationParamsT>({
        query: ({ projectId, blockerId, params }) => ({
          url: `projects/${projectId}/blockers/${blockerId}/hand_off_notes`,
          method: "POST",
          body: keysToSnakeCase(params),
        }),
        transformResponse: (res: GetProjectBlockersResponseT) => {
          SchemaValidator.validate<typeof GetProjectBlockersResponseSchema>({
            schema: GetProjectBlockersResponseSchema,
            schemaName: "GetProjectBlockersResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res.body);
        },
        onQueryStarted: ({ projectId, isAdmin, isEnterprise = false }, { dispatch, queryFulfilled }) => {
          dispatch(
            actions.setAlertMessageVisible({
              message: "Submitting. . .",
              severity: "info",
              duration: 6000,
            }),
          );

          queryFulfilled
            .then(({ data }) => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: "Success! Hand Off Notes have been updated",
                  severity: "success",
                }),
              );
              dispatch(
                projectBlockersApi.util.updateQueryData(
                  "getProjectBlockers",
                  { projectId, isEnterprise },
                  (cachedData) => {
                    cachedData.blockers = data.blockers;
                    cachedData.completedBlockers = data.completedBlockers;
                    cachedData.allBlockersComplete = data.allBlockersComplete;
                  },
                ),
              );
              if (isAdmin) {
                dispatch(actions.getAdminProjectDetails({ projectId }));
              } else {
                dispatch(actions.getProviderLeadDetails({ providerLeadId: projectId, enterprise: isEnterprise }));
              }
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: "There was an error updating the hand off notes for this project.",
                  severity: "error",
                }),
              );
            });
        },
      }),
    }),
  });

export const {
  useGetProjectBlockersQuery,
  useGetProjectBlockerQuery,
  useCreateProjectBlockerMessageMutation,
  useCreateProjectBlockerAssetMutation,
  usePrefetch: usePrefetchProjectBlockers,
  useUpdateDesignAnswersMutation,
} = projectBlockersApi;
